import React, { useRef, useState, useEffect } from 'react'
import {
  Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button, LinearProgress, TextField, Checkbox, Autocomplete, FormControl, FormLabel, Typography, Divider
} from '@mui/material'
import moment from 'moment'
import { ClassNameMap, withStyles } from '@mui/styles'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import Draggable from 'react-draggable'
import { toast } from 'react-toastify'
import stylesheet from '../detail-container.stylesheet'
import Helper from '../../../../utils/helper'
import { PatientService } from '../../../../services/patient.service'
import DialogTitleHeaderComponent from '../common/dialog-title-header.component'

interface IProps {
  openUpload: any
  input: any
  onUploadClose: any
  classes: ClassNameMap
}

const ReportDownloadComponent: React.FC<IProps> = ({ openUpload, input, onUploadClose, classes }) => {
  const defaultValues = { report_file_names: [] as string[] }
  const [formValues, setFormValues] = React.useState({ ...defaultValues });
  const patientService = new PatientService()
  const [fileLoading, setFileLoading] = useState(false)
  const downloadTaskReportRef = useRef(null) as any
  const [filesSet, setFilesSet] = useState<any[]>([]);
  const [retroDate, setRetroDate] = useState(moment().subtract(1, 'day').format('YYYY-MM-DD'))

  const checkDisabledBtn = () => formValues['report_file_names'].length == 0

  const handleRetrospectiveDownload = () => {
    setFileLoading(true)
    patientService.downloadRetrospectiveTaskReport(retroDate).then((res) => {
      Helper.handleTaskReportFile(res?.data, `retrospective_task_report_${retroDate.replace(/-/g, '_')}.xlsx`)
      setFileLoading(false)
      onUploadClose()
    }).catch((err: any) => {
      const message = err?.response?.status === 404
        ? `No assignment history found for ${retroDate}`
        : Helper.getErrorText(err)
      toast.error(message, Helper.bottom_center())
      setFileLoading(false)
    })
  }

  const handleSubmit = () => {
    setFileLoading(true)
    formValues?.report_file_names?.forEach(element => {
      patientService.downloadTaskReport(element).then((res) => {
        Helper.handleTaskReportFile(res?.data, element)
        onUploadClose()
      }).catch((err: any) => {
        toast.error(Helper.getErrorText(err), {
          ...Helper.bottom_center(),
          style: {
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }
        })
        setFileLoading(false)
        onUploadClose()
      })
    });
  }

  useEffect(() => {
    patientService.getReportFileNames().then((res: any) => {
      const fileNames = res?.data?.report_files
      let taskFileDict: any[] = []
      for (let i = 0; i < fileNames.length; i++) {
        taskFileDict = [...taskFileDict, { 'name': fileNames[i], 'display_name': fileNames[i] }]
      }
      setFilesSet(taskFileDict)
    }).catch((err: any) => {
      console.log(err)
    })
  }, [])

  const sortedFilesSet = [...filesSet]?.sort((a: any, b: any) => (a.name > b.name) ? -1 : 1)

  const handleReportFileChange = (changedData: any) => {
    if (Object.keys(changedData)?.length > 0) {
      setFormValues({ ...formValues, 'report_file_names': changedData?.map((i: any) => i.name) })
    }
  }

  return (
    <Draggable ref={downloadTaskReportRef} handle='.add-appointmenmts-dialog'>
      <Dialog
        fullWidth={true}
        maxWidth="sm"
        open={openUpload}
        onClose={() => onUploadClose()}
        aria-labelledby={'Download Task Report'}
        hideBackdrop
        disableScrollLock
        onKeyUp={(e) => {
          e.preventDefault();
          if (e.key === 'Enter' && !checkDisabledBtn())
            handleSubmit()
        }}
        sx={{
          position: "absolute",
          left: '15%',
          top: '15%'
        }}
      >
        <DialogTitle className={classes.addPatientQuestionHeaderSection} classes={{ root: 'add-appointmenmts-dialog' }}>
          <DialogTitleHeaderComponent title={input.title} handleDialogClose={onUploadClose} />
        </DialogTitle>
        <Grid item>{fileLoading && <LinearProgress />}</Grid>
        <DialogContent className={classes.downloadDailyTaskReportContent}>
          <Grid item md={12} xs={12}>
            <FormControl className={classes.formControl}>
              <FormLabel id="mutiple-checkbox-label">
                <Typography className={classes.desc} variant='subtitle1'>Select files</Typography>
              </FormLabel>
              <Autocomplete
                multiple
                id='report_file_names'
                options={sortedFilesSet}
                getOptionLabel={(option: any) => option.display_name}
                // defaultValue={recentProgramsSet()}
                disablePortal={true}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      sx={{ transform: 'scale(0.6)', padding: '2px 2px 0px 2px' }}
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      checked={selected}
                    />
                    {option.display_name}
                  </li>
                )}
                renderInput={(params: any) => (
                  <TextField {...params} variant="standard" placeholder="" />
                )}
                onChange={(event: any, newValue: any | null) => handleReportFileChange(newValue)}
                sx={{
                  width: '320px',
                  padding: '0.1rem 0rem 0rem 0.3rem !important',
                  '& .MuiAutocomplete-noOptions ': {
                    fontSize: '0.75rem !important'
                  },
                }}
              />
            </FormControl>
          </Grid>
          <Grid item md={12} xs={12} sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1 }} />
            <FormControl className={classes.formControl}>
              <FormLabel id="retrospective-date-label">
                <Typography className={classes.desc} variant='subtitle1'>Work review (what got done for a day&apos;s assigned tasks)</Typography>
              </FormLabel>
              <Grid container spacing={1} alignItems="center" sx={{ padding: '0.3rem 0rem 0rem 0.3rem' }}>
                <Grid item>
                  <TextField
                    id="retrospective-date"
                    type="date"
                    size="small"
                    variant="standard"
                    value={retroDate}
                    inputProps={{ max: moment().format('YYYY-MM-DD') }}
                    onChange={(e: any) => setRetroDate(e.target.value)}
                  />
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={!retroDate || fileLoading}
                    onClick={() => handleRetrospectiveDownload()}
                  >
                    Download retrospective
                  </Button>
                </Grid>
              </Grid>
            </FormControl>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container spacing={2} className={classes.containerPadding} justifyContent="flex-end">
            <Grid item>
              <Button
                variant="outlined"
                size="small"
                disabled={checkDisabledBtn()}
                onClick={() => handleSubmit()}
                component="label"
              >
                Download
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" size="small" onClick={onUploadClose}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </Draggable>
  )
}

export default withStyles(stylesheet)(ReportDownloadComponent)